// Global
import React, { useEffect, useRef, useState } from 'react';

// Local
import styles from './TableOfContents.module.scss';
import clsx from 'clsx';
import { BrandAndThemeProvider } from 'components/helpers/ContextProvider/BrandAndThemeContext';
import { Themes } from 'lib/themes';
import { EditingHelpText } from 'components/helpers/Editing/EditingHelpText';

export interface TableOfContentsItem {
  id: string;
  text: string;
  level: number;
}

export interface TableOfContentsProps {
  className?: string;
  heading?: string;
  containerSelector?: string;
  theme?: Themes;
  hideCount?: number;
  showMoreText?: string;
  showLessText?: string;
}

export const TableOfContents = (props: TableOfContentsProps) => {
  const {
    className,
    heading,
    containerSelector,
    theme,
    hideCount = 5,
    showMoreText = 'Show more',
    showLessText = 'Show less',
  } = props;

  // Keep in sync with `src/assets/_breakpoints.scss` ($breakpoint-desktop-min: 75rem = 1200px)
  const desktopMediaQuery = '(min-width: 1200px)';

  const [activeId, setActiveId] = useState<string>('');
  const [tocItems, setTocItems] = useState<TableOfContentsItem[]>([]);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const linkRefs = useRef<Map<string, HTMLAnchorElement | null>>(new Map());
  const pendingFocusIdRef = useRef<string | null>(null);
  const [isDesktop, setIsDesktop] = useState<boolean>(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia(desktopMediaQuery).matches;
  });

  useEffect(() => {
    if (!pendingFocusIdRef.current) return;
    if (!isExpanded) return;
    if (isDesktop) return;

    const idToFocus = pendingFocusIdRef.current;

    // Wait for the expanded links to mount.
    const raf = window.requestAnimationFrame(() => {
      linkRefs.current.get(idToFocus)?.focus();
      pendingFocusIdRef.current = null;
    });

    return () => window.cancelAnimationFrame(raf);
  }, [isDesktop, isExpanded, tocItems]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;

    const mediaQueryList = window.matchMedia(desktopMediaQuery);
    const onChange = () => setIsDesktop(mediaQueryList.matches);

    onChange();

    if (typeof mediaQueryList.addEventListener === 'function') {
      mediaQueryList.addEventListener('change', onChange);
      return () => mediaQueryList.removeEventListener('change', onChange);
    }

    mediaQueryList.addListener(onChange);
    return () => mediaQueryList.removeListener(onChange);
  }, [desktopMediaQuery]);

  useEffect(() => {
    // Auto-generate table of contents from page headings
    const resolveContainer = () => {
      if (containerSelector) {
        // Support both raw element IDs ("main-layout-content") and CSS selectors ("#main-layout-content", ".content")
        const selector = containerSelector.trim();
        if (selector.startsWith('#') || selector.startsWith('.') || selector.includes(' ')) {
          return document.querySelector(selector);
        }
        return document.getElementById(selector);
      }
      return document.querySelector('main') || document.body;
    };

    const container = resolveContainer();

    const generateItems = () => {
      if (!container) return;

      const headings = container.querySelectorAll('h2');
      const generatedItems: TableOfContentsItem[] = Array.from(headings)
        .filter((heading) => {
          // Skip the TOC's own heading
          if (heading.classList.contains(styles.heading)) return false;
          // Skip headings inside card-listing components that have excludeContentGridH2TitleFromTableOfContents enabled
          const cardListingParent = heading.closest('[data-component*="card-listing"]');
          if (cardListingParent && cardListingParent.hasAttribute('data-hide-h2-for-toc'))
            return false;
          return true;
        })
        .map((heading, index) => {
          if (!heading.id) {
            heading.id = `toc-heading-${index}`;
          }
          // Allow programmatic focus when navigating from the TOC
          heading.setAttribute('tabindex', '-1');

          const level = parseInt(heading.tagName.charAt(1));
          return {
            id: heading.id,
            text: heading.textContent || '',
            level: level - 2,
          };
        })
        .filter((item) => item.text.trim().length > 0);

      setTocItems(generatedItems);
    };

    // Run once immediately and once after paint to catch late-rendered headings
    generateItems();
    const raf = window.requestAnimationFrame(generateItems);
    return () => window.cancelAnimationFrame(raf);
  }, [containerSelector, isExpanded]);

  useEffect(() => {
    // Track scroll position and highlight active section
    if (!tocItems.length) return;

    const handleScroll = () => {
      const scrollPosition = window.scrollY + 100; // Offset for header

      // Find the currently visible heading
      let currentId = '';
      for (const item of tocItems) {
        const element = document.getElementById(item.id);
        if (element) {
          const { top } = element.getBoundingClientRect();
          const absoluteTop = top + window.scrollY;

          if (absoluteTop <= scrollPosition) {
            currentId = item.id;
          }
        }
      }

      setActiveId(currentId);
    };

    handleScroll(); // Initial check
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [tocItems]);

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    event.preventDefault();
    const element = document.getElementById(id);

    if (element) {
      const yOffset = -80; // Offset for fixed header
      const y = element.getBoundingClientRect().top + window.scrollY + yOffset;

      window.scrollTo({ top: y, behavior: 'smooth' });
      setActiveId(id);
      // Shift keyboard focus to the heading so screen readers and keyboard users
      // know where they are after activating the link (works for both click and Enter).
      element.focus({ preventScroll: true });
    }
  };

  const isKeyboardInteractionRef = useRef(false);

  const handleToggleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      isKeyboardInteractionRef.current = true;
    }
  };

  const handleToggleExpanded = () => {
    const nextExpanded = !isExpanded;
    if (nextExpanded && isKeyboardInteractionRef.current) {
      // Focus the first newly revealed item only when expanding via keyboard.
      const itemToFocus = tocItems[hideCount] || tocItems[tocItems.length - 1];
      pendingFocusIdRef.current = itemToFocus?.id || null;
    }
    isKeyboardInteractionRef.current = false;
    setIsExpanded(nextExpanded);
  };

  if (!tocItems || tocItems.length === 0) {
    return (
      <EditingHelpText componentName="Table of Contents">
        This component automatically generates a table of contents from the H2 headings on the page.
        It will appear on the published page when headings are present.
      </EditingHelpText>
    );
  }

  const showToggle = tocItems.length > hideCount;
  const shouldCollapse = !isDesktop && showToggle && !isExpanded;
  const displayItems = shouldCollapse ? tocItems.slice(0, hideCount) : tocItems;

  return (
    <BrandAndThemeProvider theme={theme}>
      <nav
        className={clsx(styles.tableOfContents, className)}
        aria-label={heading}
        data-component="content/table-of-contents"
      >
        <div className={styles.container}>
          <h2 className={styles.heading}>{heading}</h2>

          <ol className={clsx(styles.list, styles.listGrid)}>
            {displayItems.map((item) => (
              <li
                key={item.id}
                className={clsx(styles.listItem, activeId === item.id && styles.active)}
              >
                <a
                  href={`#${item.id}`}
                  className={styles.link}
                  onClick={(e) => handleClick(e, item.id)}
                  aria-current={activeId === item.id ? 'location' : undefined}
                  ref={(el) => {
                    linkRefs.current.set(item.id, el);
                  }}
                >
                  {item.text}
                </a>
              </li>
            ))}
          </ol>

          {showToggle && (
            <button
              type="button"
              className={styles.toggleButton}
              onClick={handleToggleExpanded}
              onKeyDown={handleToggleKeyDown}
              aria-expanded={isExpanded}
            >
              {isExpanded ? showLessText : showMoreText}
            </button>
          )}
        </div>
      </nav>
    </BrandAndThemeProvider>
  );
};
